'use client'

import { Animation, Card, Cell, Divider, EmptyState, Pagination, Text } from '@vinted/web-ui'
import { useTransition, MouseEvent } from 'react'

import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useAsset } from '@marketplace-web/shared/assets'
import { InfoBanner } from '@marketplace-web/info-banner/info-banner-feature'
import { UsersResp } from '@marketplace-web/search/user-search-data'

import { MEMBER_SEARCH_URL } from './constants/routes'
import UserGrid from './UserGrid'

type Props = {
  data: UsersResp
  searchText: string
}

const UserSearch = ({ data, searchText }: Props) => {
  const translate = useTranslate('user.search')
  const asset = useAsset('/assets/animations')
  const { routerPush } = useBrowserNavigation()
  const [isLoading, startTransition] = useTransition()

  const urlWithPage = (page: number): string => {
    return MEMBER_SEARCH_URL(page, searchText)
  }

  const renderPagination = () => {
    if (!data.users.length || data.pagination.total_pages <= 1) return null

    const onClick = (page: number, event: MouseEvent) => {
      event.preventDefault()
      startTransition(() => routerPush(urlWithPage(page)))
    }

    return (
      <Card>
        <Pagination
          currentPage={data.pagination.current_page}
          pageCount={data.pagination.total_pages}
          testId="user-search-pagination"
          urlBuilder={urlWithPage}
          onNextClick={onClick}
          onPrevClick={onClick}
          onPageClick={onClick}
        />
      </Card>
    )
  }

  const renderBodyContent = () => {
    if (!data?.users.length) {
      return (
        <div className="user-search__body-state--center">
          <EmptyState
            animation={<Animation animationUrl={asset('member-empty-state-96.json')} />}
            title={translate('empty_state.title')}
            body={translate('empty_state.body')}
          />
        </div>
      )
    }

    return (
      <UserGrid
        users={data.users.map(dto => ({
          id: dto.id,
          photo: dto.photo && {
            url: dto.photo.url,
            thumbnails: dto.photo.thumbnails,
          },
          login: dto.login,
          feedbackReputation: dto.feedback_reputation,
          feedbackCount: dto.feedback_count,
          isFavourite: dto.is_favourite,
          isBusiness: dto.business,
          businessAccount: dto.business_account,
        }))}
        testId="user-search-grid"
      />
    )
  }

  return (
    <>
      <InfoBanner screen="users" linkTracking={{ target: 'merge_following_banner' }} />
      <div className="user-search__container">
        <Cell title={<Text as="h2" text={translate('title')} type="title" />} styling="narrow" />
        <Divider />
        <div data-loading={isLoading} className="user-search__body">
          {renderBodyContent()}
        </div>
        {renderPagination()}
      </div>
    </>
  )
}

export default UserSearch
